import { useTranslation } from "react-i18next";
import { type Team } from "@/lib/backend/api";

type ProfileTeam = Pick<Team, "id" | "name">;

type Props = {
  /**
   * The teams the current employee belongs to. `/me` is auth-scoped, so these are always the
   * logged-in employee's OWN teams. `undefined` while the profile is still loading (or for
   * non-employees) — the section is hidden.
   */
  teams: ProfileTeam[] | undefined;
};

/**
 * Read-only "Teams" section on the employee profile (issue #372). Shows the teams the employee
 * belongs to, or an empty-state when they are in none yet. Renders nothing while `teams` is
 * undefined.
 */
export function ProfileTeamsSection({ teams }: Props) {
  const { t } = useTranslation();

  if (!teams) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-txt-primary">
        {t("routes:personal-information.teams.title", "Teams")}
      </h2>

      {teams.length === 0 ? (
        <p className="text-sm text-txt-secondary">
          {t("routes:personal-information.teams.empty", "Je zit nog niet in een team.")}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {teams.map((team) => (
            <li
              key={team.id}
              className="rounded-full bg-surface-neutral px-3 py-1 text-sm text-txt-primary"
            >
              {team.name}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
